
import { supabase } from "@/integrations/supabase/client";

/**
 * Check if the current user is an admin using the new user_roles table and has_role function.
 */
export async function isAdmin(userId: string | undefined | null): Promise<boolean> {
  if (!userId) return false;
  // Call the Postgres function directly to check role
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error) {
    console.error("Error checking admin role:", error);
    return false;
  }
  return !!data;
}

// Fetch all admin users (from the user_roles table)
export async function fetchAdminUsers() {
  // Join with profiles for friendly info if available in your app
  return supabase
    .from("user_roles")
    .select("user_id, role, created_at");
}

/**
 * Add a role to a user
 * Note: This should only be called after checking the current user is an admin
 */
export async function addUserRole(userId: string, role: 'admin' | 'user') {
  return supabase
    .from("user_roles")
    .insert({
      user_id: userId,
      role: role
    });
}

/**
 * Remove a role from a user
 * Note: This should only be called after checking the current user is an admin
 */
export async function removeUserRole(userId: string, role: 'admin' | 'user') {
  return supabase
    .from("user_roles")
    .delete()
    .match({
      user_id: userId,
      role: role
    });
}

// Fetch change logs with pagination and filtering options
export async function fetchChangeLogs(options?: {
  limit?: number;
  offset?: number;
  componentType?: string;
  actionType?: string;
  fromDate?: string;
  toDate?: string;
}) {
  let query = supabase
    .from("change_logs")
    .select("id, component_type, component_name, action, description, details, created_by, created_at");

  // Apply filters if provided
  if (options?.componentType) {
    query = query.eq("component_type", options.componentType);
  }
  
  if (options?.actionType) {
    query = query.eq("action", options.actionType);
  }
  
  if (options?.fromDate) {
    query = query.gte("created_at", options.fromDate);
  }
  
  if (options?.toDate) {
    query = query.lte("created_at", options.toDate);
  }
  
  // Add pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }
  
  // Order by created_at in descending order
  return query.order("created_at", { ascending: false });
}

// Record a change log (no changes necessary here)
export async function recordChangeLog(log: {
  componentType: string;
  componentName: string;
  action: string;
  description: string;
  details?: any;
  createdBy?: string;
}) {
  return supabase.from("change_logs").insert({
    component_type: log.componentType,
    component_name: log.componentName,
    action: log.action,
    description: log.description,
    details: log.details || {},
    created_by: log.createdBy
  });
}
